import { freshCsrfToken, redirectToSignIn } from '../lib/fresh_csrf_token.mjs'

export default class extends HTMLElement {
  connectedCallback () {
    this.button = this.querySelector('#apply-discount-button')
    this.applyDiscountHandler ||= this.applyDiscountCode.bind(this)
    this.button?.removeEventListener('click', this.applyDiscountHandler)
    this.button?.addEventListener('click', this.applyDiscountHandler)
  }

  disconnectedCallback () {
    this.button?.removeEventListener('click', this.applyDiscountHandler)
  }

  get reducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  // Puts the offer banner at the top of the page and scrolls up so the buyer sees it land,
  // followed by the repriced cards.
  showBanner (html) {
    const slot = document.getElementById('discount-banner-slot')
    if (!slot || !html) return

    const template = document.createElement('template')
    template.innerHTML = html.trim()
    slot.replaceChildren(template.content)

    window.scrollTo({ top: 0, behavior: this.reducedMotion ? 'auto' : 'smooth' })
  }

  markApplied (code) {
    const input = this.querySelector('#global-discount-code')
    input.value = code
    this.classList.add('is-applied')
    this.button.textContent = 'Applied'

    this.inputHandler ||= () => {
      this.classList.remove('is-applied')
      this.button.textContent = 'Apply'
    }
    input.addEventListener('input', this.inputHandler, { once: true })
  }

  formatPrice (price) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'decimal'
    }).format(price)
  }

  updatePlanUI (planElement, discountedPlan) {
    // Calculate price per credit
    const pricePerCredit = parseFloat(discountedPlan.price) / parseFloat(discountedPlan.credits)

    // Update the UI
    planElement.querySelector('.final-price-amount').textContent = this.formatPrice(pricePerCredit)
    planElement.querySelector('.plan-price').textContent = this.formatPrice(discountedPlan.price)
    planElement.querySelector('.plan-credits').textContent = discountedPlan.credits
    // ... other updates ...
  }

  async applyDiscountCode (event) {
    event.preventDefault()
    const discountCode = this.querySelector('#global-discount-code').value
    // The page may have been restored by Back with a stale token; ask for the current one.
    const { token: csrfToken, signedIn } = await freshCsrfToken()
    if (!signedIn) {
      redirectToSignIn()
      return
    }

    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' }
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken

    fetch('/credit_plans/check_discount_code', {
      method: 'POST',
      headers,
      body: JSON.stringify({ discount_code: discountCode })
    })
      .then(response => response.json())
      .then(data => {
        const errorMessage = this.querySelector('#error-message')
        const errorMessageText = this.querySelector('#error-message-text')

        if (data.success) {
          // A code can cover several packs; swap every card it covers that is on this page.
          let replaced = 0
          for (const { original_plan_id: planId, replacement_html: html } of data.replacements || []) {
            const originalPlanElement = document.getElementById(`plan-${planId}`)
            const template = document.createElement('template')
            template.innerHTML = html.trim()
            const replacement = template.content.firstElementChild

            if (originalPlanElement && replacement) {
              replacement.classList.add('discount-applied')
              replacement.style.setProperty('--discount-delay', `${250 + replaced * 90}ms`)
              originalPlanElement.replaceWith(replacement)
              replaced += 1
            }
          }

          if (replaced > 0) {
            this.showBanner(data.banner_html)
            this.markApplied(data.discount_code)
          }
          errorMessage.classList.add('hidden')
        } else {
          errorMessageText.textContent = data.error || 'An error occurred'
          errorMessage.classList.remove('hidden')
        }
      })
      .catch(error => {
        console.error('Error:', error)
        const errorMessage = this.querySelector('#error-message')
        const errorMessageText = this.querySelector('#error-message-text')
        errorMessageText.textContent = 'An error occurred while processing your request'
        errorMessage.classList.remove('hidden')
      })
  }
}
